<template>
    <v-app-bar color="#084cad" elevation="0">
      <v-btn icon variant="text" @click="$router.back()" class="text-white">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <v-img
        src="https://mvexpress.mx/images/logosapp/logomv1.png"
        alt="Logo MV"
        max-width="200"
        contain
        class="mx-auto"
      />

      <div
        v-if="esMovil"
        class="d-flex align-center"
        style="margin-left: auto; margin-right: 10px; position: relative"
      >
        <v-btn
          icon
          variant="text"
          class="text-white"
          @click="activarNotificaciones"
          :disabled="store.activas"
        >
          <v-icon size="28">
            {{ store.activas ? "mdi-bell-ring" : "mdi-bell-outline" }}
          </v-icon>
        </v-btn>

        <div
          v-if="store.nueva"
          style="
            position: absolute;
            top: 8px;
            right: 8px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: red;
          "
        ></div>
      </div>

      <v-btn
        icon
        variant="text"
        class="text-white"
        @click="llamarTelefono('+529611416440')"
      >
        <v-icon size="28">mdi-phone</v-icon>
      </v-btn>
    </v-app-bar>
</template>

<script setup>
import { onMounted, computed } from "vue";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import Swal from "sweetalert2";
import { useNotificacionesStore } from "../../stores/notificacioneStore"; // Ajusta la ruta si es necesario
import { useUserStore } from "../../stores/userStore"; // Ajusta la ruta si es necesario
import api from "@/services/api"; // Ajusta la ruta si es necesario
import { useRouter } from "vue-router";

const store = useNotificacionesStore();
const userStore = useUserStore();
const router = useRouter();

// Detectar si estamos en Android o iOS
const esMovil = computed(() => Capacitor.isNativePlatform());

const llamarTelefono = (numero) => {
  if (esMovil.value) {
    window.open(`tel:${numero}`, "_system");
  } else {
    window.location.href = `tel:${numero}`;
  }
};

// Función para guardar el token en el servidor
const enviarTokenAlServidor = async (tokenValue) => {
  try {
    const userId = userStore.user?.usuario || userStore.user?.id;
    if (!userId) {
      console.warn("No hay ID de usuario, el token no se guardará correctamente.");
    }

    await api.post("/api/notificaciones/guardar-token", {
      token: tokenValue,
      dispositivo: Capacitor.getPlatform(),
      grupo: "general",
      usuario_id: userId,
    });

    console.log("✅ Token actualizado en el backend");
    store.activar(); // Marca como activa en el store local
  } catch (err) {
    console.error("Error guardando token en servidor:", err);
  }
};

// Listener para el registro de notificaciones
const configurarListeners = async () => {
  if (!esMovil.value) return;

  // Limpiar listeners previos
  await PushNotifications.removeAllListeners();

  // 1. Registro exitoso
  PushNotifications.addListener("registration", (token) => {
    console.log("Token FCM recibido:", token.value);
    enviarTokenAlServidor(token.value);
  });

  // 2. Error en registro
  PushNotifications.addListener("registrationError", (err) => {
    console.error("Error en registro de Push:", err);
  });

  // 3. 🔥 RECIBIR NOTIFICACIÓN (DISEÑO PERSONALIZADO TIPO TARJETA)
  PushNotifications.addListener("pushNotificationReceived", (notification) => {
    console.log("Notificación recibida en foreground:", notification);
    
    // Extraer datos (con valores por defecto por seguridad)
    const title = notification.title || '¡Oferta Especial!';
    const body = notification.body || 'Tienes una nueva promoción esperando.';

    // Mostrar la alerta personalizada
    Swal.fire({
      // HTML Personalizado para replicar el diseño de la imagen
      html: `
        <div class="mv-notification-card">
          <div class="custom-notification-header">
            <div class="header-left">
               <i class="mdi mdi-bell-ring-outline notification-icon"></i>
               <span class="app-name">MVExpress</span>
            </div>
            <span class="notification-time">ahora</span>
          </div>
          <div class="custom-notification-body">
            <h3 class="notification-title">${title}</h3>
            <p class="notification-text">${body}</p>
          </div>
          <div class="mv-notification-actions">
             <button id="btn-ver-ofertas" class="mv-btn confirm">Ver Ofertas</button>
             <button id="btn-despues" class="mv-btn cancel">Después</button>
          </div>
        </div>
      `,
      toast: true,
      position: 'top', // Aparece arriba centrado (o usa 'top-end' para derecha)
      showConfirmButton: false, // Ocultamos botones por defecto para usar los personalizados
      showCancelButton: false,
      background: 'transparent', // 🔥 Quitamos fondo blanco del contenedor padre

      timer: 8000, // Duración en pantalla
      timerProgressBar: true,

      // Clases CSS personalizadas para quitar estilos por defecto de SweetAlert
      customClass: {
        popup: 'mv-notification-popup-container',
      },
      
      // Detener timer al pasar el mouse
      didOpen: (toast) => {
        const btnVer = toast.querySelector('#btn-ver-ofertas');
        const btnDespues = toast.querySelector('#btn-despues');

        btnVer?.addEventListener('click', () => { Swal.close(); router.push('/Home'); });
        btnDespues?.addEventListener('click', () => Swal.close());

        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    });
  });

  // 4. Acción al tocar la notificación (App cerrada o en segundo plano)
  // Este evento se dispara automáticamente al abrir la app desde la notificación
  PushNotifications.addListener("pushNotificationActionPerformed", (notification) => {
    console.log("👆 Usuario tocó la notificación:", notification);
    // Manejar redirección si hay deep link
    router.push('/Home');
  });
};

const activarNotificaciones = async () => {
  if (!esMovil.value) return;

  try {
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive === "granted") {
      await PushNotifications.register();
      
      Swal.fire({
        icon: "success",
        title: "Notificaciones activadas",
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false,
      });
    } else {
      throw new Error("Permiso denegado por el usuario");
    }
  } catch (err) {
    Swal.fire("Aviso", "No pudimos activar las notificaciones: " + err.message, "warning");
  }
};

onMounted(async () => {
  if (esMovil.value) {
    configurarListeners();
    const status = await PushNotifications.checkPermissions();
    if (status.receive === "granted") {
      await PushNotifications.register();
    } else {
      // 🔔 Si no están activas, avisar al usuario para que las active
      Swal.fire({
        title: '🔔 Activa las notificaciones',
        text: 'Para recibir alertas de tus pedidos y ofertas exclusivas.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Activar ahora',
        cancelButtonText: 'Más tarde',
        confirmButtonColor: '#ef6c00',
      }).then((result) => {
        if (result.isConfirmed) {
          activarNotificaciones();
        }
      });
    }
  }
});
</script>

<style>
/* --- ESTILOS PARA LA NOTIFICACIÓN PERSONALIZADA --- */

/* 1. Contenedor Principal Transparente */
div:where(.swal2-container) .mv-notification-popup-container {
  padding: 0 !important; /* Quitar padding blanco por defecto */
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
  width: 90% !important; /* Ancho responsivo */
  max-width: 360px !important;
  font-family: 'Roboto', sans-serif;
}

/* 2. Tarjeta Interna (Da la forma y sombra) */
.mv-notification-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* 2. Encabezado Azul */
.custom-notification-header {
  background-color: #ef6c00; /* Naranja Ofertas */
  color: white;
  padding: 8px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.notification-icon {
  font-size: 16px;
  background: white;
  color: #ef6c00;
  border-radius: 4px;
  padding: 1px 3px;
}

.app-name {
  font-weight: 600;
  font-size: 13px;
}

.notification-time {
  font-size: 11px;
  opacity: 0.8;
}

/* 3. Cuerpo Blanco */
.custom-notification-body {
  padding: 15px 15px 5px 15px;
  background-color: white;
  text-align: left;
}

.notification-title {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 5px 0;
  color: #333;
}

.notification-text {
  font-size: 13px;
  color: #555;
  margin: 0;
  line-height: 1.4;
}

/* 4. Botones */
.mv-notification-actions {
  padding: 10px 15px 15px 15px !important;
  display: flex !important;
  gap: 10px;
  justify-content: space-between !important;
  background-color: white;
}

.mv-btn {
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 0;
  flex: 1; /* Ocupa la mitad */
  cursor: pointer;
  transition: opacity 0.2s;
}

.mv-btn:active { opacity: 0.8; }

.mv-btn.confirm {
  background-color: #ef6c00;
  color: white;
}

.mv-btn.cancel {
  background-color: #f5f5f5;
  color: #555;
}

/* Estilos originales de tu app bar */
v-main {
  padding-bottom: env(safe-area-inset-bottom, 25px);
}
</style>